import { useEffect, useMemo, useState } from "react";
import { vocabularize } from "src/Common/vocab";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import BranchBar from "src/Components/branchbar/BranchBar";
import BranchService from "src/Services/API/branch.service";
import NotificationService from "src/Services/API/notification.service";
import UserService from "src/Services/API/user.service";
import { can, handleError } from "src/Common/function";
import ChannelTabs from "./components/channeltabs";
import ReceiversDrawer from "./components/receiversdrawer";
import RuleGroup from "./components/rulegroup";
import { CHANNELS, FLOWS, getChannel, ruleKey } from "./constants";

// Stable identity so the drawer's "reset selection" effect doesn't re-fire on
// every render of a rule that has no receivers yet.
const EMPTY_IDS = [];

const parseRecipients = (recipientList) =>
  recipientList ? String(recipientList).split(",").filter(Boolean) : [];

const NotificationCenter = () => {
  const canManage = can("notification:manage");
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));

  const [activeType, setActiveType] = useState(CHANNELS[0].type);
  const [userArray, setUserArray] = useState([]);
  const [branchName, setBranchName] = useState("");
  // Every rule the page knows about, keyed by `type:flow:event`.
  const [rules, setRules] = useState({});
  const [loadedTypes, setLoadedTypes] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  // `drawerRule` outlives `drawerOpen` so the panel keeps its title and
  // breadcrumb while it slides out.
  const [drawerRule, setDrawerRule] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const channel = getChannel(activeType);
  const isLoading = !loadedTypes.includes(activeType);

  useEffect(() => {
    // A different branch is a different set of rules, so drop what's cached.
    setRules({});
    setLoadedTypes([]);

    // getAll is user:read-gated; the recipient picker only needs display
    // fields (id/name/email/role name), so use the ungated picker endpoint —
    // this page is notification:read-gated, which doesn't imply user:read.
    UserService.getUsersForAssign(null, branch)
      .then((response) => {
        if (response.status == 200) {
          setUserArray(response.data);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  }, [branch]);

  useEffect(() => {
    BranchService.getAll()
      .then((response) => {
        const match = (response.data ?? []).find((item) => item.id == branch);
        setBranchName(match?.name ?? "");
      })
      .catch(() => setBranchName(""));
  }, [branch]);

  // The API only resolves one rule per request, so hydrate a channel by firing
  // one request per row and folding the answers into `rules`. Fetched per tab
  // and cached, so switching back is free.
  useEffect(() => {
    if (!branch) return undefined;
    if (loadedTypes.includes(activeType)) return undefined;

    let cancelled = false;
    const target = getChannel(activeType);
    const requests = FLOWS.flatMap(({ flow }) =>
      target.statuses[flow].map((status) =>
        NotificationService.get(activeType, flow, status.value, branch)
          .then((response) => ({
            key: ruleKey(activeType, flow, status.value),
            ids: parseRecipients(response?.data?.recipientList),
          }))
          // A rule that was never configured resolves to "nobody", not an error.
          .catch(() => ({ key: ruleKey(activeType, flow, status.value), ids: [] }))
      )
    );

    Promise.all(requests).then((results) => {
      if (cancelled) return;
      setRules((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          next[result.key] = result.ids;
        });
        return next;
      });
      setLoadedTypes((prev) => (prev.includes(activeType) ? prev : [...prev, activeType]));
    });

    return () => {
      cancelled = true;
    };
  }, [activeType, branch, loadedTypes]);

  const usersById = useMemo(() => {
    const map = new Map();
    userArray.forEach((user) => map.set(String(user.id), user));
    return map;
  }, [userArray]);

  // Recipients are stored as ids; drop any that no longer resolve to a user
  // (deleted accounts, or someone moved off this branch).
  const receiversFor = (flow, event) =>
    (rules[ruleKey(activeType, flow, event)] ?? EMPTY_IDS)
      .map((id) => {
        const user = usersById.get(String(id));
        if (!user) return null;
        const subtitle = user.roles?.[0]?.name ?? user.email;
        return { id: user.id, label: subtitle ? `${user.name} (${subtitle})` : user.name };
      })
      .filter(Boolean);

  const drawerSelectedIds = useMemo(() => {
    if (!drawerRule) return EMPTY_IDS;
    return rules[ruleKey(drawerRule.type, drawerRule.flow, drawerRule.status.value)] ?? EMPTY_IDS;
  }, [drawerRule, rules]);

  const drawerContext = useMemo(() => {
    if (!drawerRule) return null;
    const flowLabel = FLOWS.find((item) => item.flow === drawerRule.flow)?.label ?? "";
    return {
      crumbs: [branchName, getChannel(drawerRule.type).label, vocabularize(flowLabel), vocabularize(drawerRule.status.name)].filter(Boolean),
    };
  }, [drawerRule, branchName]);

  const openDrawer = (flow, status) => {
    setDrawerRule({ type: activeType, flow, status });
    setDrawerOpen(true);
  };

  const saveReceivers = (users) => {
    if (!drawerRule) return;
    const ids = users.map((user) => user.id);
    const recipientList = ids.length > 0 ? ids.join() : null;

    setSaving(true);
    NotificationService.create(drawerRule.type, drawerRule.flow, drawerRule.status.value, recipientList, branch)
      .then((response) => {
        if (response.status == 200 || response.status == 201) {
          const key = ruleKey(drawerRule.type, drawerRule.flow, drawerRule.status.value);
          setRules((prev) => ({ ...prev, [key]: parseRecipients(response.data?.recipientList) }));
          toast.success("Receivers updated.");
          setDrawerOpen(false);
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="w-full">
      <ReceiversDrawer
        open={drawerOpen}
        context={drawerContext}
        userArray={userArray}
        selectedUserIdArray={drawerSelectedIds}
        saving={saving}
        onCancel={() => setDrawerOpen(false)}
        onSave={saveReceivers}
      />

      <div className="px-5 py-2 border-b border-borderColor">
        <BranchBar allEnabled={false} />
      </div>

      <div className="p-5 flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <p className="font-OpenSansSemiBold text-[18px] leading-[26px] text-titleColor">Notification Center</p>
          <p className="font-OpenSansRegular text-[14px] leading-[20px] text-subTitleColor">
            Choose who gets notified, for each status and delivery channel.
          </p>
        </div>

        <ChannelTabs activeType={activeType} onChange={setActiveType} />

        <div className="flex flex-col gap-6">
          {FLOWS.map(({ flow, label, description }) => {
            const key = `${activeType}:${flow}`;
            return (
              <RuleGroup
                key={key}
                title={vocabularize(label)}
                description={vocabularize(description)}
                statuses={channel.statuses[flow]}
                channel={channel}
                receiversFor={(event) => receiversFor(flow, event)}
                loading={isLoading}
                canManage={canManage}
                collapsed={!!collapsed[key]}
                onToggle={() => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))}
                onEdit={(status) => openDrawer(flow, status)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
